<template>
  <div>
    <div
      class="modal fade overflow-hidden"
      id="mapModal"
      tabindex="-1"
      aria-labelledby="mapModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
          <div class="modal-body position-relative p-0">
            <!-- Map container -->
            <div
              id="map"
              ref="mapContainer"
              style="width: 100%; height:100vh; position: relative;"
            >
              <!-- Live stats row + elevation profile with a playback-synced head dot (matches the
                   iOS/Android flyover). Replaces the old cut-off Distance/Elevation box. -->
              <FlyoverElevationProfile
                v-if="showLabels && flyoverCoords.length > 1"
                :coords="flyoverCoords"
                :breaks="flyoverBreaks"
                :progress="flyoverProgress"
                :distance-label="String(currentDistance)"
                :elevation-label="String(currentElevation)"
                :peaks-label="peaksLabel"
              />
            </div>
            <div v-if="mapInitialized" class="flyover-toolbar">
              <div class="flyover-primary-buttons">
                <button class="btn btn-secondary flyover-pill-button" @click="openMapsDialog">
                  {{ $t('shared.maps') }}
                </button>

                <!-- Recordings only: a hike's video (its trailer) is shared from the hike's own share
                     options, not from the 3D preview. -->
                <button
                  v-if="isRecordedTrail"
                  class="btn btn-primary flyover-pill-button"
                  @click="openShareVideo"
                >
                  <span class="material-icons" style="font-size: 18px; vertical-align: middle; margin-right: 6px;">movie</span>
                  {{ $t('flyover_preview.share_video') }}
                </button>
              </div>

              <div class="speed-buttons">
                <button
                  v-for="speed in speedOptions"
                  :key="speed"
                  :class="['btn', 'flyover-pill-button', playbackSpeed === speed ? 'btn-primary' : 'btn-secondary']"
                  @click="setPlaybackSpeed(speed)"
                >
                  {{ speed }}x
                </button>
              </div>

              <div v-if="animationController" class="control-buttons">
                <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button" @click="handleRewind">
                  <span class="material-icons">fast_rewind</span>
                </button>

                <button class="btn btn-primary rounded-circle play-button d-flex align-items-center control-button" @click="togglePlayback">
                  <span v-if="isPlaying" class="material-icons">pause</span>
                  <span v-else class="material-icons">play_arrow</span>
                </button>

                <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button" @click="handleFastForward">
                  <span class="material-icons">fast_forward</span>
                </button>
              </div>
            </div>

            <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button close-button" data-bs-dismiss="modal">
              <span class="material-icons">close</span>
            </button>
          </div>
        </div>
      </div>
      <LoadingDialog :show-loading-dialog="loading"/>
    </div>
    <Teleport to="body">
      <BaseMapStylesDialog
        dialog-id="flyover-map-styles-dialog"
        map-instance-name="flyover_map"
        :maps-only="true"
        :show3-d-toggle="false"
        :force-full-style-reload="true"
      />
    </Teleport>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import $ from "jquery";
// Import the previewTrail helper (ensure the path is correct)
import { previewTrail } from "../../utils/helpers/TrailPreview";
import { buildFlyoverRoute } from "../../utils/helpers/flyoverRoute";
import { createPeakOverlay } from "../../utils/helpers/flyoverPeakOverlay";
import { isNaturalStep, cumulativeKm } from "../../utils/helpers/flyoverPeaks";
import { createPhotoOverlay } from "../../utils/helpers/flyoverPhotoOverlay";

// GraphQL clients/queries
import graph_client from "../../utils/graphql/graph_client";
import { GetHike } from "../../utils/graphql/hikes-graph";
import { GetPtv2PlannedTrailLineString } from "../../utils/graphql/planned-trail-graph";
import { GetRecordedTrailLineStringById } from "../../utils/graphql/recorded-trail-graph";
import { MeasurementPrefUtility } from "../../utils/helpers/MeasurementPref";
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import { Modal } from "bootstrap";
import { claimReviewPromptTrigger } from '../../utils/reviewPromptTrigger';
import BaseMapStylesDialog from "../base_map_dialog/BaseMapStylesDialog.vue";
import FlyoverElevationProfile from "./FlyoverElevationProfile.vue";
import { resolveFlyoverMapInit } from "./flyoverMapInit";
import { hikeFlyoverSegments, lineSegmentsFrom, parseFeatureCollection } from "../../utils/helpers/flyoverGeometry";
import { HiikerMapStyle } from "../../utils/models/MapStyles";
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";
import { recordingShareConfigFromDataset } from "../flyover_share/recordingShareConfig";

// Analytics entry point for every share opened from this screen (see flyover_share/shareAnalytics.ts).
// Both of openShareVideo's paths — passed-in recording config, page dataset — are the same entry point
// as far as "where did this share start" goes.
const SHARE_SOURCE = "trail_3d_preview";

export default {
  name: "FlyoverPreview",
  components: {LoadingDialog, BaseMapStylesDialog, FlyoverElevationProfile},
  props: {
    title: { type: String, default: "3D Flyover" }
  },
  data() {
    return {
      loading: false,
      mapInitialized: false,
      // True once the fullscreen modal has finished opening (its container is at final size). Map
      // creation waits for this so Mapbox measures the real size instead of its 400x300 default.
      modalShown: false,
      defaultCenter: [-74.5, 40],
      featureCollection: null,
      segments: [], // Array of linestring segments
      currentSegmentIndex: 0,
      segmentControllers: [], // Animation controllers for each segment
      preloadedSegments: new Map(), // Cache for preloaded segment data
      mapboxToken:
        "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2xydDBwMTU4MDFmdDJwbzd5NjFxMmlyciJ9.Q0bXbghB7N2Ji93oLrAJng",
      currentDistance: 0,
      currentElevation: 0,
      showLabels: true,
      // Whole-route vertices [lng, lat, elev?] + live playback progress (0..1) driving the elevation
      // profile's head dot. Set when a route is loaded; progress is read from the animation controller.
      flyoverCoords: [],
      // Vertex indices where the walk restarts (a gap between two of the hike's routes).
      flyoverBreaks: [],
      flyoverProgress: 0,
      animationController: null,
      isPlaying: true,
      finishedAnimation: false,
      playbackSpeed: 1,
      speedOptions: [1, 2, 3, 4, 5, 10],
      controllerSequence: 0,
      activeControllerId: null,
      // True only when this preview is showing a recording — gates the "Share video" toolbar button.
      isRecordedTrail: false,
      // Share config for the current recording (recordingId/shareUrl/stats), passed in by whoever
      // opened the preview so the in-preview "Share video" button works from any entry point.
      recordedTrailShareConfig: null,
      // The summits a recording's OWNER bagged on it (recording.flyoverPeaks), celebrated in place as
      // the flyover reaches each one — the same overlay the flyover video draws. Empty for hikes.
      flyoverPeaks: [],
      peaksReached: 0,
      peaksTotal: 0,
      // Where the flyover stops to show the walker's photos (recording.flyoverPhotoStops): the same
      // stops, and the same overlay, as the flyover video. Empty for hikes.
      flyoverPhotoStops: [],
    };
  },
  computed: {
    peaksLabel() {
      if (!this.peaksTotal) return "";
      return this.$t("flyover_preview.peaks_reached", { reached: this.peaksReached, total: this.peaksTotal });
    },
  },
  mounted() {
    this.$store.commit("mapStyles/setCurrentMapStyle", HiikerMapStyle.satellite());

    // Keep the same global function name for backward compatibility
    // Now it loads Hike data instead of Trail
    window.showMapModalForTrail = trail_id => {
      this.showModal();
      this.loading = true;
      graph_client.request(GetHike, { id: trail_id }).then(data => {
        // Use routes-only for flyover preview to show just the main route
        const hike = data.hike;
        // Routes-only geometry, falling back to the hike's full geometry when it carries no lines —
        // a hike whose planned trails are ALL alternates/spurs (or which has none yet) filters down to
        // zero features, and there was then nothing to animate: no route, and no stats row or
        // elevation profile, both of which are gated on the flyover having coordinates.
        const { segments, usedFallback } = hikeFlyoverSegments(
          hike && hike.geojsonRoutesOnlyString,
          hike && hike.geojsonMultilinestringsWithStartEndPointsString
        );
        this.segments = segments;
        if (usedFallback) {
          console.warn("[flyover] hike has no route-styled geometry; flying its full geometry instead");
        }
        // Kept as context for bounds/etc. Null when there is no geometry at all, which is the one case
        // that legitimately has nothing to draw.
        this.featureCollection = segments.length > 0
          ? { type: "FeatureCollection", features: segments }
          : null;
        this.initializeMapWhenReady();
      });
    };

    window.showMapModalForPlannedTrail = planned_trail_id => {
      this.showModal();
      this.loading = true;
      graph_client
        .request(GetPtv2PlannedTrailLineString, { id: planned_trail_id })
        .then(data => {
          this.featureCollection = JSON.parse(
            data.ptv2PlannedTrailById.lineStringFeatureCollection
          );
          this.initializeMapWhenReady();
        });
    };

    window.showMapModalForRecordedTrail = (recorded_trail_id, shareConfig = null) => {
      this.showModal();
      this.isRecordedTrail = true;
      this.recordedTrailShareConfig = shareConfig;
      this.loading = true;
      graph_client
        .request(GetRecordedTrailLineStringById, { input: recorded_trail_id })
        .then(data => {
          this.featureCollection = JSON.parse(data.recording.lineString);
          this.flyoverPeaks = data.recording.flyoverPeaks || [];
          this.flyoverPhotoStops = data.recording.flyoverPhotoStops || [];
          this.initializeMapWhenReady();
        });
    };

    // Accept raw GeoJSON FeatureCollection directly (used by plan trail)
    window.showMapModalForCoordinates = (featureCollection) => {
      this.showModal();
      this.loading = true;
      // Same extraction as the hike entry, and tolerant of a string, a parsed object or junk — this
      // used to JSON.parse without a guard and then deref `.features` on the result.
      this.segments = lineSegmentsFrom(featureCollection);
      this.featureCollection = parseFeatureCollection(featureCollection);
      this.initializeMapWhenReady();
    };

    $("#mapModal").on("shown.bs.modal", () => {
      // The modal has finished its open transition, so the container is now at its final size. Gate
      // map creation on this: building the Mapbox map before this point makes it fall back to a
      // 400x300 default canvas anchored top-left, which is why the route appeared "in a corner".
      this.modalShown = true;
      if (window.flyover_map) {
        window.flyover_map.resize();
      }
    });

    $("#mapModal").on("hidden.bs.modal", () => {
      this.teardownPeakOverlay();
      this.teardownPhotoOverlay();
      this.resetAnimationControllers();
      this.segments = [];
      this.currentSegmentIndex = 0;
      this.preloadedSegments.clear();
      if (window.flyover_map) {
        window.flyover_map.remove();
        window.flyover_map = null;
      }
      this.mapInitialized = false;
      this.modalShown = false;
      this.currentDistance = 0;
      this.currentElevation = 0;
      this.flyoverCoords = [];
      this.flyoverBreaks = [];
      this.flyoverProgress = 0;
      this.finishedAnimation = false;
      this.isPlaying = true;
      this.playbackSpeed = 1;
    });
  },
  beforeUnmount() {
    $("#mapModal").off("shown.bs.modal");
    $("#mapModal").off("hidden.bs.modal");
    delete window.showMapModalForRecordedTrail;
    delete window.showMapModalForPlannedTrail;
    delete window.showMapModalForTrail;
    delete window.showMapModalForCoordinates;
    this.resetAnimationControllers();
    if (window.flyover_map) {
      window.flyover_map.remove();
      window.flyover_map = null;
    }
  },
  methods: {
    showModal() {
      // Default to non-recording; the recorded-trail entry flips this on to reveal the Share button.
      this.isRecordedTrail = false;
      // Only the recorded-trail entry has peaks to celebrate; it sets them once its query resolves.
      this.flyoverPeaks = [];
      this.teardownPeakOverlay();
      this.flyoverPhotoStops = [];
      this.teardownPhotoOverlay();
      this.modalShown = false;
      const element = document.getElementById("mapModal");
      const modal = new Modal(element);
      modal.show();

      // Track mixpanel event for 3D preview (support both Hike/Trail naming during migration)
      if (window.mixpanel) {
        const name = window.hike_name || window.trail_name || window.trailName;
        const id = window.hike_id || window.trail_id || window.trailId || window.id;
        window.mixpanel.track('3D Preview Shown', {
          'Hike Name': name,
          'Hike ID': id,
          'Trail Name': name,
          'Trail ID': id
        });
      }

      // Same moment, scored for the web review prompt. Server-side the trigger name is
      // re-validated and deduped once per session, so this can safely fire on every open.
      claimReviewPromptTrigger('3d_preview_shown');
    },
    // ---- Bagged-peak celebrations (shared overlay: utils/helpers/flyoverPeakOverlay.ts) ----
    // Live, the overlay runs on wall time and is positioned from the map's `render` event, so the
    // DOM moves in the same frame as the GL draw. There is no slow-down through summits here: the
    // live flyover has no speed envelope (TrailPreview applies one only when rendering a video).
    setUpPeakOverlay(coords) {
      this.teardownPeakOverlay();
      const map = window.flyover_map;
      if (!map || !this.flyoverPeaks.length || !coords || coords.length < 2) return;
      const cumKm = cumulativeKm(coords);
      const pref = MeasurementPrefUtility.preference();
      const overlay = createPeakOverlay(
        map, this.flyoverPeaks, coords, cumKm,
        (m) => pref.smallUnitFormatter(m),
        () => this.peakCardBounds(),
        { container: this.$refs.mapContainer, scale: 1.1, pixelRatio: window.devicePixelRatio || 1, zIndex: 5 }
      );
      if (!overlay) return;
      this.peakOverlay = overlay;
      this.peakTotalKm = cumKm[cumKm.length - 1] || 0;
      this.peakT = 0;
      this.peakPrevT = 0;
      this.peakNatural = true;
      this.peaksTotal = overlay.total;
      this.peaksReached = 0;
      this.peakRenderHandler = () => this.updatePeakOverlay();
      map.on("render", this.peakRenderHandler);
      map.triggerRepaint();
    },
    teardownPeakOverlay() {
      if (this.peakRenderHandler && window.flyover_map) window.flyover_map.off("render", this.peakRenderHandler);
      this.peakRenderHandler = null;
      if (this.peakOverlay) this.peakOverlay.destroy();
      this.peakOverlay = null;
      this.peaksTotal = 0;
      this.peaksReached = 0;
    },
    // From the flyover's per-frame progress callback (also fired by fast-forward/rewind). A skip
    // anywhere since the last draw makes that draw a skip: summits it passes plant silently.
    notePeakProgress() {
      if (!this.peakOverlay) return;
      const t = this.animationController && typeof this.animationController.getProgress === "function"
        ? this.animationController.getProgress()
        : 0;
      this.peakNatural = this.peakNatural && isNaturalStep(this.peakPrevT, t, this.peakTotalKm);
      this.peakPrevT = t;
      this.peakT = t;
    },
    updatePeakOverlay() {
      const overlay = this.peakOverlay;
      if (!overlay) return;
      const now = performance.now() - (this.photoHeldMs || 0);
      overlay.update(this.peakT, now, this.peakNatural);
      this.peakNatural = true;
      const reached = overlay.reachedCount();
      if (reached !== this.peaksReached) this.peaksReached = reached;
      // A paused flyover stops repainting the map; keep it going until a celebration has played out.
      if (overlay.isAnimating(now)) requestAnimationFrame(() => window.flyover_map && window.flyover_map.triggerRepaint());
    },
    // ---- Photo stops (shared overlay: utils/helpers/flyoverPhotoOverlay.ts) ----
    // Built BEFORE the flyover so it can be told where to stop and for how long. Photos load in the
    // background; a stop keeps its place even if a photo fails, so the flight and the overlay agree.
    // Returns the cameraOptions half previewTrail needs.
    setUpPhotoOverlay(coords) {
      this.teardownPhotoOverlay();
      const map = window.flyover_map;
      if (!map || !this.flyoverPhotoStops.length || !coords || coords.length < 2) return {};
      const overlay = createPhotoOverlay(
        map, this.flyoverPhotoStops, coords, cumulativeKm(coords),
        () => this.photoBounds(),
        { container: this.$refs.mapContainer, scale: 0.55, zIndex: 5, stageZIndex: 8 }
      );
      if (!overlay) return {};
      overlay.preload({ dropFailed: false });
      this.photoOverlay = overlay;
      this.photoFracs = overlay.fracs();
      this.photoHeldMs = 0;
      this.photoLastRender = performance.now();
      this.photoRenderHandler = () => this.updatePhotoOverlay();
      map.on("render", this.photoRenderHandler);
      return { stopFractions: this.photoFracs, stopHoldMs: overlay.durations() };
    },
    teardownPhotoOverlay() {
      if (this.photoRenderHandler && window.flyover_map) window.flyover_map.off("render", this.photoRenderHandler);
      this.photoRenderHandler = null;
      if (this.photoOverlay) this.photoOverlay.destroy();
      this.photoOverlay = null;
      this.photoFracs = [];
      this.photoHeldMs = 0;
    },
    updatePhotoOverlay() {
      const overlay = this.photoOverlay;
      const controller = this.animationController;
      if (!overlay || !controller) return;
      const now = performance.now();
      const active = typeof controller.getStop === "function" ? controller.getStop() : null;
      // The summits' clock stands still while a photo is up, so a celebration carries on after it.
      if (active) this.photoHeldMs += now - this.photoLastRender;
      this.photoLastRender = now;
      const t = typeof controller.getProgress === "function" ? controller.getProgress() : 0;
      overlay.update({ active: () => active, wasShown: (i) => t >= this.photoFracs[i] });
    },
    // Where a full photo may sit inside the map: under the toolbar, above the stats panel.
    photoBounds() {
      const b = this.peakCardBounds();
      return { left: b.left + 8, top: b.top + 8, right: b.right - 8, bottom: b.bottom - 8 };
    },
    // Where a summit card may sit inside the map: under the toolbar, above the stats panel.
    peakCardBounds() {
      const container = this.$refs.mapContainer;
      const box = container.getBoundingClientRect();
      const toolbar = this.$el.querySelector(".flyover-toolbar");
      const panel = container.querySelector(".flyover-profile-bar");
      const top = toolbar ? toolbar.getBoundingClientRect().bottom - box.top + 8 : 72;
      const bottom = panel ? panel.getBoundingClientRect().top - box.top - 8 : box.height - 24;
      return { left: 12, top, right: box.width - 12, bottom };
    },
    createControllerId() {
      this.controllerSequence += 1;
      return this.controllerSequence;
    },
    destroyAnimationController(controller) {
      if (!controller) return;
      if (typeof controller.destroy === "function") {
        controller.destroy();
        return;
      }
      if (typeof controller.stop === "function") {
        controller.stop();
      }
    },
    resetAnimationControllers() {
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);
      this.animationController = null;
      this.segmentControllers.forEach(controller => {
        this.destroyAnimationController(controller);
      });
      this.segmentControllers = [];
    },
    applyPlaybackSpeed(controller = this.animationController) {
      if (!controller || typeof controller.setSpeedMultiplier !== "function") return;
      controller.setSpeedMultiplier(this.playbackSpeed);
    },
    // Mirror the animation controller's progress (0..1) onto the elevation profile's head dot. Called
    // from the per-frame progress callback, so it also stays in sync after fast-forward/rewind/restart.
    syncFlyoverProgress() {
      const p = this.animationController && typeof this.animationController.getProgress === "function"
        ? this.animationController.getProgress()
        : 0;
      this.flyoverProgress = p;
    },
    setPlaybackSpeed(speed) {
      this.playbackSpeed = speed;
      this.applyPlaybackSpeed();
    },
    // Open the flyover share wizard over this preview (stacked), reusing the recording data the share
    // button on the page already carries. Closing/finishing share returns to this 3D preview.
    openShareVideo() {
      const show = window.showFlyoverShare;
      if (typeof show !== "function") return;
      // The share video must match the style the user is looking at right now (they may have switched
      // it via the "Maps" dialog), so every path carries the preview's current map style — this is
      // what makes the wizard re-generate instead of replaying a stale render after a style change.
      const mapStyleUrl = this.currentMapStyleUrl();
      // Prefer the config passed in when the preview was opened (works from any entry point, e.g. the
      // recordings list). Fall back to the page's "Generate 3D flyover" dataset / globals otherwise.
      if (this.recordedTrailShareConfig && this.recordedTrailShareConfig.recordingId) {
        show({ ...this.recordedTrailShareConfig, mapStyleUrl, source: SHARE_SOURCE });
        return;
      }
      const btn = document.querySelector(".generate-3d-flyover");
      show({
        ...recordingShareConfigFromDataset(btn && btn.dataset, {
          recordingId: window.recordingId,
          recordingShareUrl: window.recordingShareUrl,
          recordingName: window.recordingName,
        }),
        mapStyleUrl,
        source: SHARE_SOURCE,
      });
    },
    openMapsDialog() {
      if (window.user) {
        const mapStylesDialog = document.getElementById("flyover-map-styles-dialog");
        if (mapStylesDialog) {
          Modal.getOrCreateInstance(mapStylesDialog).show();
        }
      } else {
        window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
      }
    },
    currentMapStyleUrl() {
      return this.$store.state.mapStyles.currentMapStyle?.styleUrl || HiikerMapStyle.satellite().styleUrl;
    },
    // Create the map only once the fullscreen modal has finished opening. If the data resolved before
    // the open transition completed, defer to the `shown.bs.modal` event; otherwise init straight away.
    // Building it earlier lets Mapbox measure a not-yet-sized container and fall back to a 400x300
    // canvas pinned top-left — the cause of the flyover starting "in a corner".
    initializeMapWhenReady() {
      // Decision is a pure helper (unit-tested in flyoverMapInit.test.ts); this method only carries
      // out the DOM/event side effects for each outcome.
      const action = resolveFlyoverMapInit(this.mapInitialized, this.modalShown);
      if (action === "skip") return;
      if (action === "init") {
        this.initializeMap();
        return;
      }
      $("#mapModal").one("shown.bs.modal", () => this.initializeMap());
    },
    initializeMap() {
      if (this.mapInitialized) return;
      mapboxgl.accessToken = this.mapboxToken;
      window.flyover_map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: this.currentMapStyleUrl(),
        zoom: 5,
        pitch: 60,
        bearing: 0,
        antialias: true,
        // Support both historic window.lonlat and newer window.lonLat keys
        center: (window.lonlat || window.lonLat || [0.0, 0.0]),
      });
      window.flyover_map.on("style.load", () => {
        if (!window.flyover_map.getSource("mapbox-dem")) {
          window.flyover_map.addSource("mapbox-dem", {
            type: "raster-dem",
            url: "mapbox://mapbox.mapbox-terrain-dem-v1",
            tileSize: 512,
            maxzoom: 14
          });
        }
        window.flyover_map.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });
        // Resize BEFORE rendering so the intro camera frames against the real container size, not a
        // stale one, even if the style resolves faster than the layout settles.
        window.flyover_map.resize();
        this.renderFeatureCollection();
      });
      window.flyover_map.addControl(new mapboxgl.NavigationControl(), "bottom-right");
      this.mapInitialized = true;
    },
    clearLayers() {
      if (!window.flyover_map) return;
      if (window.flyover_map.getLayer("feature-shadow")) {
        window.flyover_map.removeLayer("feature-shadow");
      }
      if (window.flyover_map.getLayer("feature")) {
        window.flyover_map.removeLayer("feature");
      }
      if (window.flyover_map.getSource("feature-data")) {
        window.flyover_map.removeSource("feature-data");
      }
      if (window.flyover_map.getLayer("progressTrail")) {
        window.flyover_map.removeLayer("progressTrail");
      }
      // Both layers must go before the source: GL JS silently refuses to remove a source any layer
      // still references, and a surviving source makes the replay skip re-adding the route line.
      if (window.flyover_map.getLayer("progressTrailBorder")) {
        window.flyover_map.removeLayer("progressTrailBorder");
      }
      if (window.flyover_map.getSource("progressTrail")) {
        window.flyover_map.removeSource("progressTrail");
      }
    },
    renderFeatureCollection() {
      this.loading = false;
      if (!window.flyover_map) return;

      // Handle new segment-based rendering
      if (this.segments && this.segments.length > 0) {
        this.renderSegmentedTrail();
        return;
      }

      // Fallback to old rendering for non-segmented trails
      if (!this.featureCollection) {
        const center = window.lonLat || this.defaultCenter;
        window.flyover_map.flyTo({
          center: center,
          zoom: 12,
          pitch: 60,
          bearing: 0
        });
        return;
      }
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);
      this.animationController = null;
      this.clearLayers();
      let geojson, coordinates = [];
      if (typeof this.featureCollection === "string") {
        try {
          geojson = JSON.parse(this.featureCollection);
        } catch (e) {
          console.error("Error parsing feature collection:", e);
          return;
        }
      } else {
        geojson = this.featureCollection;
      }
      if (geojson.type === "FeatureCollection") {
        for (const feature of geojson.features) {
          if (feature.geometry && feature.geometry.coordinates) {
            if (feature.geometry.type === "LineString") {
              coordinates = feature.geometry.coordinates;
              break;
            } else if (feature.geometry.type === "Point") {
              coordinates = [feature.geometry.coordinates];
              break;
            } else if (feature.geometry.type === "Polygon") {
              coordinates = feature.geometry.coordinates[0];
              break;
            }
          }
        }
      } else if (geojson.type === "Feature") {
        if (geojson.geometry && geojson.geometry.coordinates) {
          if (geojson.geometry.type === "LineString") {
            coordinates = geojson.geometry.coordinates;
          } else if (geojson.geometry.type === "Point") {
            coordinates = [geojson.geometry.coordinates];
          } else if (geojson.geometry.type === "Polygon") {
            coordinates = geojson.geometry.coordinates[0];
          }
        }
      }
      window.flyover_map.addSource("feature-data", { type: "geojson", data: geojson });
      // `geojson.features || [geojson]` was the bug: an EMPTY array is truthy, so the fallback never
      // fired and `features[0].geometry` threw on a collection with no features — aborting the render
      // before it set flyoverCoords, which is what the stats row and profile are gated on.
      const features = Array.isArray(geojson.features)
        ? (geojson.features.length > 0 ? geojson.features : [])
        : [geojson];
      const feature = features[0];
      const geometryType = feature && feature.geometry ? feature.geometry.type : null;
      window.flyover_map.addLayer({
        id: "feature-shadow",
        type: geometryType === "Point" ? "circle" : "line",
        source: "feature-data",
        paint:
          geometryType === "Point"
            ? { "circle-radius": 12, "circle-color": "#000", "circle-opacity": 0.4, "circle-blur": 3 }
            : { "line-color": "#DB2B35", "line-width": 4 }
      });
      window.flyover_map.addLayer({
        id: "feature",
        type: geometryType === "Point" ? "circle" : "line",
        source: "feature-data",
        paint:
          geometryType === "Point"
            ? { "circle-radius": 8, "circle-color": "#FF4500", "circle-opacity": 1 }
            : { "line-color": "#DB2B35", "line-width": 8, "line-opacity": 1 }
      });
      if (geometryType === "LineString" && typeof previewTrail === "function" && coordinates.length > 0) {
        this.flyoverCoords = coordinates;
        const controllerId = this.createControllerId();
        // Against `coordinates` too, like the summits below: the one line this path flies.
        const photoStops = this.setUpPhotoOverlay(coordinates);
        this.animationController = previewTrail(
          window.flyover_map,
          coordinates,
          "medium",
          {},
          (distKm, elevM) => {
            const convertedDistance = MeasurementPrefUtility.preference().bigUnitFormatter(distKm);
            const convertedElv = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
            this.currentDistance = convertedDistance;
            this.currentElevation = convertedElv;
            this.syncFlyoverProgress();
            this.notePeakProgress();
          },
          photoStops
        );
        this.activeControllerId = controllerId;
        // Against `coordinates` — the one line this path actually flies. A recording stored as
        // several LineStrings flies only its first here, so a summit on a later one sits off the
        // flown line and placePeaks leaves it out rather than celebrating it at the wrong moment.
        this.setUpPeakOverlay(coordinates);
        this.applyPlaybackSpeed();
        this.isPlaying = true;
        this.finishedAnimation = false;
        this.animationController.then(() => {
          if (this.activeControllerId !== controllerId) return;
          this.isPlaying = false;
          this.finishedAnimation = true;
        });
      } else {
        this.fitToCoordinates(coordinates);
      }
    },
    fitToCoordinates(coordinates) {
      if (!coordinates || coordinates.length === 0 || !window.flyover_map) return;
      const bounds = new mapboxgl.LngLatBounds();
      coordinates.forEach(coord => bounds.extend(coord));
      window.flyover_map.fitBounds(bounds, { padding: 50, maxZoom: 15, pitch: 60 });
    },
    renderSegmentedTrail() {
      this.currentSegmentIndex = 0;
      this.resetAnimationControllers();
      this.clearLayers();

      // The walk the flyover follows: the hike's routes oriented to follow one another, run
      // together where they meet, broken where they genuinely don't (see flyoverRoute.ts). Merging
      // routes in the hike editor says nothing about the direction each was drawn in — routes
      // merged end-to-end meet END to END — and taken as authored the flyover jumped back to the
      // far tip of each one, drawing a straight line the length of the route across the map.
      const route = buildFlyoverRoute(this.segments.map(seg => seg.geometry.coordinates));
      const multiLineCoordinates = route.strokes.map(stroke => stroke.coords);

      const fullTrailFeature = {
        type: "Feature",
        geometry: {
          type: "MultiLineString",
          coordinates: multiLineCoordinates
        }
      };

      window.flyover_map.addSource("feature-data", {
        type: "geojson",
        data: fullTrailFeature
      });

      window.flyover_map.addLayer({
        id: "feature-shadow",
        type: "line",
        source: "feature-data",
        paint: { "line-color": "#DB2B35", "line-width": 4 }
      });

      window.flyover_map.addLayer({
        id: "feature",
        type: "line",
        source: "feature-data",
        paint: { "line-color": "#DB2B35", "line-width": 8, "line-opacity": 1 }
      });

      // Relive: a single flyover over the WHOLE route instead of playing each segment separately,
      // so the flat-2D->3D intro, the orbiting follow, and the 2D zoom-out outro frame the entire
      // hike as one experience. The strokes go in as strokes, not flattened into one line: that is
      // what keeps the marker off the gaps and the pen up across them.
      if (route.coords.length < 2) {
        this.finishedAnimation = true;
        this.isPlaying = false;
        return;
      }
      this.flyoverCoords = route.coords;
      this.flyoverBreaks = route.breaks;

      const controllerId = this.createControllerId();
      this.animationController = previewTrail(
        window.flyover_map,
        multiLineCoordinates,
        "medium",
        {},
        (distKm, elevM) => {
          this.currentDistance = MeasurementPrefUtility.preference().bigUnitFormatter(distKm);
          this.currentElevation = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
          this.syncFlyoverProgress();
        }
      );
      this.activeControllerId = controllerId;
      this.applyPlaybackSpeed();
      this.isPlaying = true;
      this.finishedAnimation = false;
      this.animationController.then(() => {
        if (this.activeControllerId !== controllerId) return;
        this.isPlaying = false;
        this.finishedAnimation = true;
      });
    },
    playSegment(segmentIndex, startFromEnd = false) {
      if (segmentIndex >= this.segments.length) {
        this.finishedAnimation = true;
        this.isPlaying = false;
        return;
      }

      this.currentSegmentIndex = segmentIndex;
      const segment = this.segments[segmentIndex];

      // Get previous segment's ending state for smooth transition
      let previousEndState = null;
      if (segmentIndex > 0 && this.segmentControllers[segmentIndex - 1] && !startFromEnd) {
        previousEndState = this.segmentControllers[segmentIndex - 1].getEndState();
      }

      // If starting from end (rewinding from next segment), get next segment's starting state
      let nextStartState = null;
      if (startFromEnd && segmentIndex < this.segments.length - 1 && this.segmentControllers[segmentIndex + 1]) {
        nextStartState = this.segmentControllers[segmentIndex + 1].getStartState ?
          this.segmentControllers[segmentIndex + 1].getStartState() : null;
      }

      // Create animation controller for this segment
      const cameraOptions = {};

      if (startFromEnd && nextStartState) {
        // Starting from end when rewinding from next segment
        cameraOptions.initialBearing = nextStartState.bearing;
        cameraOptions.initialPitch = nextStartState.pitch;
        cameraOptions.startFromEnd = true;
      } else if (previousEndState) {
        // Normal forward progression
        cameraOptions.initialBearing = previousEndState.bearing;
        cameraOptions.initialPitch = previousEndState.pitch;
      }

      const controllerId = this.createControllerId();
      this.animationController = previewTrail(
        window.flyover_map,
        segment.geometry.coordinates,
        "medium",
        {},
        (distKm, elevM) => {
          // Calculate total distance including previous segments
          let totalDistance = distKm;
          for (let i = 0; i < segmentIndex; i++) {
            const prevSegment = this.segments[i];
            const prevLength = this.calculateSegmentLength(prevSegment.geometry.coordinates);
            totalDistance += prevLength;
          }

          const convertedDistance = MeasurementPrefUtility.preference().bigUnitFormatter(totalDistance);
          const convertedElv = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
          this.currentDistance = convertedDistance;
          this.currentElevation = convertedElv;
        },
        cameraOptions
      );

      this.activeControllerId = controllerId;
      this.segmentControllers[segmentIndex] = this.animationController;
      this.applyPlaybackSpeed();
      this.isPlaying = true;
      this.finishedAnimation = false;

      // When segment finishes, play the next one
      this.animationController.then(() => {
        if (this.activeControllerId !== controllerId) return;
        const isLastSegment = segmentIndex >= this.segments.length - 1;

        if (this.isPlaying) {
          if (!isLastSegment) {
            this.destroyAnimationController(this.segmentControllers[segmentIndex]);
            this.playSegment(segmentIndex + 1);

            if (segmentIndex + 2 < this.segments.length) {
              this.preloadSegment(segmentIndex + 2);
            }
          } else {
            this.finishedAnimation = true;
            this.isPlaying = false;
          }
        } else {
          if (!isLastSegment) {
            this.currentSegmentIndex = segmentIndex + 1;
            this.animationController = null;
          } else {
            this.finishedAnimation = true;
          }
        }
      });
    },
    preloadSegment(segmentIndex) {
      // Preprocess segment data in background
      if (segmentIndex < this.segments.length) {
        const segment = this.segments[segmentIndex];
        // Store preprocessed data for faster loading
        this.preloadedSegments.set(segmentIndex, {
          coordinates: segment.geometry.coordinates,
          preprocessed: true
        });
      }
    },
    calculateSegmentLength(coordinates) {
      let totalLength = 0;
      for (let i = 1; i < coordinates.length; i++) {
        const p1 = coordinates[i - 1];
        const p2 = coordinates[i];
        const R = 6371; // Earth radius in km
        const dLat = (p2[1] - p1[1]) * Math.PI / 180;
        const dLon = (p2[0] - p1[0]) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(p1[1] * Math.PI / 180) * Math.cos(p2[1] * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        totalLength += R * c;
      }
      return totalLength;
    },
    jumpToSegment(segmentIndex, startFromEnd = false) {
      if (segmentIndex < 0 || segmentIndex >= this.segments.length) return;

      // Stop current animation
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);

      // Clear any pending segment loads
      this.segmentControllers.forEach((controller, index) => {
        if (controller && index !== segmentIndex) {
          this.destroyAnimationController(controller);
        }
      });

      // Play the target segment
      this.playSegment(segmentIndex, startFromEnd);

      // Preload adjacent segments
      if (segmentIndex > 0) {
        this.preloadSegment(segmentIndex - 1);
      }
      if (segmentIndex < this.segments.length - 1) {
        this.preloadSegment(segmentIndex + 1);
      }
    },
    handleFastForward() {
      if (!this.animationController) return;

      // Check if we're near the end of current segment
      if (this.segments && this.segments.length > 0) {
        // Try to fast forward within current segment
        this.animationController.fastForward();

        // If at end of current segment and there's a next segment, jump to it
        // This would require checking animation progress, which we'll handle in the animation callback
      } else {
        // Use regular fast forward for non-segmented trails
        this.animationController.fastForward();
      }
    },
    handleRewind() {
      if (!this.animationController) return;

      if (this.segments && this.segments.length > 0) {
        // Try to rewind within current segment
        this.animationController.rewind();

        // If at beginning of current segment and there's a previous segment
        // we could implement jumping to previous segment here
      } else {
        // Use regular rewind for non-segmented trails
        this.animationController.rewind();
      }
    },
    togglePlayback() {
      const controller = this.animationController;

      if (!controller) {
        if (this.finishedAnimation) {
          this.restartAnimation();
        } else if (this.segments && this.segments.length > 0) {
          const nextIndex = Math.min(this.currentSegmentIndex, this.segments.length - 1);
          this.playSegment(nextIndex || 0);
          this.isPlaying = true;
          this.finishedAnimation = false;
        }
        return;
      }

      const progress = typeof controller.getProgress === 'function' ? controller.getProgress() : null;

      if (this.finishedAnimation && (progress === null || progress >= 1)) {
        this.restartAnimation();
        return;
      }

      if (this.isPlaying) {
        controller.pause();
        this.isPlaying = false;
        this.finishedAnimation = false;
      } else {
        if (progress !== null && progress >= 1) {
          this.restartAnimation();
        } else {
          controller.resume();
          this.isPlaying = true;
          this.finishedAnimation = false;
        }
      }
    },
    restartAnimation() {
      this.activeControllerId = null;
      this.renderFeatureCollection();
      this.finishedAnimation = false;
      this.isPlaying = true;
    },
  },
};
</script>

<style scoped>
.modal-fullscreen {
  width: 100vw;
  max-width: none;
  height: 100vh;
  margin: 0;
}
.modal-fullscreen .modal-content {
  height: 100vh;
  border: 0;
  border-radius: 0;
}
.close-button {
  position: absolute;
  top: 1em;
  right: 1em;
  z-index: 1000;
  width: 40px;
  height: 40px !important;
}
.play-button {
  position: relative;
  z-index: 1000;
}
.flyover-toolbar {
  position: absolute;
  top: 1em;
  right: 5.5em;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  max-width: calc(100% - 8.5em);
  z-index: 1000;
}
.speed-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.flyover-primary-buttons {
  display: flex;
  gap: 10px;
}
.flyover-pill-button {
  min-height: 40px;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-weight: 600;
}
.control-buttons {
  display: flex;
  gap: 10px;
}
.control-button {
  width: 40px;
  height: 40px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}
.spinner-border {
  width: 4rem;
  height: 4rem;
}

@media (max-width: 768px) {
  /* Stack the toolbar into three single-line rows instead of letting the pills wrap raggedly:
       row 1 — speed pills (sit on the same line as the cancel/X button, top-right)
       row 2 — playback controls (rewind / play / forward)
       row 3 — Maps + Share video
     The toolbar starts at the top so its first row lines up with the X; `right: 60px` keeps that
     row clear of the X. The speed row scrolls horizontally if the pills overflow. */
  .flyover-toolbar {
    top: 1em;
    left: 10px;
    right: 60px;
    max-width: none;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    gap: 8px;
  }
  .speed-buttons {
    order: 1;
    flex-wrap: nowrap;
    justify-content: flex-start;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 2px;
  }
  .speed-buttons::-webkit-scrollbar {
    display: none;
  }
  .speed-buttons .flyover-pill-button {
    flex: 0 0 auto;
  }
  .control-buttons {
    order: 2;
    justify-content: flex-start;
  }
  .flyover-primary-buttons {
    order: 3;
    justify-content: flex-start;
  }
}
</style>
